import { useEffect, useState } from "react";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { DriveItem } from "../types";
import { getIconByMime, formatSize, getFriendlyFileType } from "../helpers";

/** A recycle-bin entry: a soft-deleted drive item with retention metadata. */
export interface RecycleBinItem extends DriveItem {
  deleted_at?: string | Date | null;
  expires_at?: string | Date | null;
  days_remaining?: number;
}

interface RecycleBinViewProps {
  rows: RecycleBinItem[];
  /** Whether to cap the height and scroll (more than one page of items). */
  scroll: boolean;
  onRestore: (items: RecycleBinItem[]) => void;
  onPermanentDelete: (items: RecycleBinItem[]) => void;
}

/**
 * Tabular listing of items currently in the recycle bin. Rows can be selected
 * individually or all at once, then restored or permanently deleted in bulk.
 * Each row also exposes single-item "Restore" / "Delete" actions. Items left
 * here are auto-purged 30 days after deletion.
 */
const RecycleBinView = ({
  rows,
  scroll,
  onRestore,
  onPermanentDelete,
}: RecycleBinViewProps) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Drop any selected ids that are no longer present (e.g. after a refresh).
  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => rows.some((row) => row.id === id)),
    );
  }, [rows]);

  const formatDate = (value?: string | Date | null) =>
    value ? new Date(value).toLocaleDateString() : "N/A";

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const allSelected = rows.length > 0 && selectedIds.length === rows.length;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : rows.map((row) => row.id));
  };

  const selectedItems = rows.filter((row) => selectedIds.includes(row.id));

  if (rows.length === 0) {
    return (
      <div className="text-center mt-4">
        <p className="text-muted">The recycle bin is empty.</p>
      </div>
    );
  }

  return (
    <>
      {selectedIds.length > 0 && (
        <div className="d-flex align-items-center justify-content-between mb-2 p-2 bg-light rounded">
          <span className="fw-medium">{selectedIds.length} selected</span>
          <div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary me-2"
              onClick={() => onRestore(selectedItems)}
            >
              <i className="ti ti-restore me-1" /> Restore Selected
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              data-bs-toggle="modal"
              data-bs-target="#purge_file_modal"
              onClick={() => onPermanentDelete(selectedItems)}
            >
              <i className="ti ti-trash me-1" /> Delete Selected
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          maxHeight: scroll ? "700px" : "none",
          overflowY: scroll ? "auto" : "visible",
          paddingRight: "8px",
          position: "relative",
        }}
      >
        <table className="table table-hover">
          <thead className="bg-light">
            <tr>
              <th style={{ width: "40px" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  title="Select all"
                />
              </th>
              <th className="file-name">Name</th>
              <th className="file-type">Type</th>
              <th className="file-size">Size</th>
              <th className="file-date">Deleted On</th>
              <th className="file-date">Auto-delete In</th>
              <th className="file-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const daysRemaining = item.days_remaining ?? 30;
              const isExpiringSoon = daysRemaining <= 7;
              const isSelected = selectedIds.includes(item.id);

              return (
                <tr key={item.id} className={isSelected ? "table-active" : ""}>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isSelected}
                      onChange={() => toggleSelect(item.id)}
                    />
                  </td>
                  <td className="file-name">
                    <ImageWithBasePath
                      src={`assets/img/icons/${getIconByMime(item)}`}
                      alt={item.name}
                      width={18}
                      height={18}
                    />
                    <span className="ms-2">{item.name}</span>
                  </td>
                  <td className="file-types">
                    {item.type === 1
                      ? "Folder"
                      : getFriendlyFileType(item.mime_type)}
                  </td>
                  <td className="file-size">
                    {item.type === 1 ? "-" : formatSize(Number(item.size))}
                  </td>
                  <td className="file-date">{formatDate(item.deleted_at)}</td>
                  <td className="file-date">
                    <span
                      className={`badge ${
                        isExpiringSoon
                          ? "bg-danger-light text-danger"
                          : "bg-info-light text-info"
                      }`}
                    >
                      {daysRemaining} day{daysRemaining === 1 ? "" : "s"}
                    </span>
                  </td>
                  <td className="file-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary me-2"
                      title="Restore"
                      onClick={() => onRestore([item])}
                    >
                      <i className="ti ti-restore me-1" /> Restore
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title="Delete permanently"
                      data-bs-toggle="modal"
                      data-bs-target="#purge_file_modal"
                      onClick={() => onPermanentDelete([item])}
                    >
                      <i className="ti ti-trash me-1" /> Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default RecycleBinView;
